import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { LayoutDashboard, Factory, Boxes, Fuel, UserCheck, CalendarClock, Users, LogOut, Mic, Truck, Package, Coins, LineChart, PackagePlus, Sparkles, Palette, UserCog, Menu, X } from "lucide-react";

const NAV = [
  { to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/admin/chat", label: "Ask Sango AI", icon: Sparkles, testid: "nav-chat", accent: true },
  { to: "/admin/production", label: "Production", icon: Factory, testid: "nav-production" },
  { to: "/admin/materials", label: "Materials", icon: Boxes, testid: "nav-materials" },
  { to: "/admin/purchases", label: "Purchases", icon: PackagePlus, testid: "nav-purchases" },
  { to: "/admin/dispatch", label: "Dispatch", icon: Truck, testid: "nav-dispatch" },
  { to: "/admin/stock", label: "Stock", icon: Package, testid: "nav-stock" },
  { to: "/admin/genset", label: "Genset", icon: Fuel, testid: "nav-genset" },
  { to: "/admin/attendance", label: "Attendance", icon: UserCheck, testid: "nav-attendance" },
  { to: "/admin/schedule", label: "Schedule", icon: CalendarClock, testid: "nav-schedule" },
  { to: "/admin/catalog", label: "Color Card", icon: Palette, testid: "nav-catalog" },
  { to: "/admin/users", label: "Users", icon: Users, testid: "nav-users" },
  { to: "/admin/account", label: "Account", icon: UserCog, testid: "nav-account" },
  { to: "/admin/costs", label: "Costs", icon: Coins, testid: "nav-costs", adminOnly: true },
  { to: "/admin/reports", label: "Reports", icon: LineChart, testid: "nav-reports", adminOnly: true },
];

function SidebarContent({ user, onLogout, onNav }) {
  return (
    <div className="flex flex-col h-full">
      <div className="p-6 border-b border-[#D8D8D5]">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E6E6A]">Sango Terrazzo</div>
        <div className="font-display text-lg font-black tracking-tighter text-[#1C1C1A]">Ops Console</div>
        <div className="text-[9px] tracking-[0.25em] uppercase text-[#C85A42] mt-1">Stronger · Better · Forever</div>
      </div>
      <nav className="p-3 flex-1 space-y-1 overflow-y-auto">
        {NAV.filter(n => !n.adminOnly || user?.role === "admin").map(({ to, label, icon: Icon, end, testid, accent }) => (
          <NavLink
            key={to} to={to} end={end} data-testid={testid} onClick={onNav}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                isActive
                  ? "bg-[#1C1C1A] text-white"
                  : accent
                    ? "text-[#C85A42] hover:bg-[#C85A42]/10"
                    : "text-[#1C1C1A] hover:bg-[#E8E8E6]"
              }`
            }
          >
            <Icon className="w-4 h-4" />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-[#D8D8D5] space-y-2">
        <div className="text-xs text-[#6E6E6A]">Signed in as</div>
        <div className="text-sm font-medium text-[#1C1C1A]">{user?.name}</div>
        <div className="text-[10px] tracking-[0.2em] uppercase text-[#6E6E6A]">{user?.role}</div>
        <Button variant="ghost" size="sm" onClick={onLogout} data-testid="admin-logout" className="w-full justify-start gap-2 text-[#C85A42] hover:text-[#C85A42]">
          <LogOut className="w-4 h-4" /> Sign out
        </Button>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen flex bg-[#F9F9F8]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-60 shrink-0 border-r border-[#D8D8D5] bg-white flex-col">
        <SidebarContent user={user} onLogout={logout} onNav={() => {}} />
      </aside>

      {/* Mobile top bar + drawer */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 bg-white border-b border-[#D8D8D5] flex items-center justify-between px-4 h-14">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="mobile-menu" className="h-10 w-10">
              <Menu className="w-5 h-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-64">
            <SidebarContent user={user} onLogout={logout} onNav={() => setOpen(false)} />
          </SheetContent>
        </Sheet>
        <div className="font-display font-black tracking-tighter text-[#1C1C1A]">SANGO</div>
        <Button variant="ghost" size="icon" onClick={() => nav("/worker")} className="h-10 w-10">
          <Mic className="w-5 h-5" />
        </Button>
      </div>

      {/* Main */}
      <main className="flex-1 min-w-0 pt-14 lg:pt-0">
        <Outlet />
      </main>
    </div>
  );
}
